// Utility functions for formatting numbers, currencies, percentages, and parsing formatted strings
export const formatNumber = (value, options = {}) => {
  const {
    decimals = 2,
    currency = false,
    currencySymbol = "₦",
    compact = false,
    locale = "en-US",
  } = options;

  const num = parseFloat(value);

  if (isNaN(num)) return value;

  // Compact notation (e.g., 1.5M, 2.3K)
  if (compact) {
    if (num >= 1_000_000_000) {
      return `${currency ? currencySymbol : ""}${(num / 1_000_000_000).toFixed(
        1
      )}B`;
    }
    if (num >= 1_000_000) {
      return `${currency ? currencySymbol : ""}${(num / 1_000_000).toFixed(
        1
      )}M`;
    }
    if (num >= 1_000) {
      return `${currency ? currencySymbol : ""}${(num / 1_000).toFixed(1)}K`;
    }
  }

  // Standard formatting with thousand separators
  const formatted = num.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return currency ? `${currencySymbol}${formatted}` : formatted;
};

export const formatCurrency = (value, options = {}) => {
  return formatNumber(value, { ...options, currency: true });
};

export const formatCompact = (value, options = {}) => {
  return formatNumber(value, { ...options, compact: true, decimals: 1 });
};

export const formatPercentage = (value, options = {}) => {
  const { decimals = 1, includeSymbol = true } = options;
  const num = parseFloat(value);

  if (isNaN(num)) return value;

  const formatted = num.toFixed(decimals);
  return includeSymbol ? `${formatted}%` : formatted;
};

export const parseFormattedNumber = (formattedValue) => {
  if (typeof formattedValue === "number") return formattedValue;

  // Remove currency symbols, commas, and other non-numeric characters except decimal point
  const cleanedValue = String(formattedValue).replace(/[₦$,\s]/g, "");

  return parseFloat(cleanedValue) || 0;
};

export const formatNumberCustom = (value, options = {}) => {
  const {
    decimals = 2,
    thousandSeparator = ",",
    decimalSeparator = ".",
  } = options;

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  const [integerPart, decimalPart] = num.toFixed(decimals).split(".");

  // Add thousand separators
  const formattedInteger = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousandSeparator
  );

  return decimals > 0
    ? `${formattedInteger}${decimalSeparator}${decimalPart}`
    : formattedInteger;
};

//Basic formatting with thousand separators
formatNumber(1234567); // "1,234,567.00"
formatNumber(1234567, { decimals: 0 }); // "1,234,567"

// Currency formatting
formatCurrency(1234567); // "₦1,234,567.00"
formatCurrency(1234567, { decimals: 0 }); // "₦1,234,567"
formatCurrency(1234567, { currencySymbol: "$" }); // "$1,234,567.00"

// Compact notation
formatCompact(1500000); // "1.5M"
formatCompact(1500000, { currency: true }); // "₦1.5M"
formatCompact(2300); // "2.3K"

// Percentage formatting
formatPercentage(85.456); // "85.5%"
formatPercentage(85.456, { decimals: 2 }); // "85.46%"

// Custom separators (European style)
formatNumberCustom(1234567.89, {
  thousandSeparator: ".",
  decimalSeparator: ",",
}); // "1.234.567,89"

// Format date helper
export const formatDate = (dateString) => {
  // Handle null, undefined, or empty string
  // console.log("Formatting date:", dateString);
  if (!dateString) {
    return "N/A";
  }

  // Handle microseconds by removing extra precision beyond milliseconds
  // Converts: 2025-12-03T22:03:53.000000Z → 2025-12-03T22:03:53.000Z
  const normalizedDateString = dateString.replace(/(\.\d{3})\d+Z$/, "$1Z");

  const date = new Date(normalizedDateString);

  // Validate the date
  if (isNaN(date.getTime())) {
    console.error("Invalid date:", dateString);
    return "Invalid Date";
  }

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${month} ${day}, ${year} ${displayHours}:${minutes} ${ampm}`;
};

// Test cases:
// console.log(formatDate("2025-12-03T22:03:53.000000Z"));
// Output: Dec 3, 2025 10:03 PM (in UTC)

// console.log(formatDate("2025-12-03T22:03:53.123456Z"));
// Output: Dec 3, 2025 10:03 PM

// console.log(formatDate("2025-12-03T14:30:00.000Z"));
// Output: Dec 3, 2025 2:30 PM

// Format customer name helper
export const formatCustomerName = (user) => {
  if (!user) return "Unknown Customer";
  const firstName = user.first_name || "";
  const lastName = user.last_name || "";
  return `${firstName} ${lastName}`.trim() || "Unknown Customer";
};
export const getStatusColor = (status) => {
  switch (status) {
    case "Incoming":
    case "Archived":
      return { color: "#F59E0B", backgroundColor: "#FEF3C7" };
    case "Confirmed":
      return { color: "#10B981", backgroundColor: "#D1FAE5" };
    case "Processing":
      return { color: "#3B82F6", backgroundColor: "#DBEAFE" };
    case "Available":
      return { color: "#8B5CF6", backgroundColor: "#EDE9FE" };
    case "Pickup":
    case "Picked Up":
      return { color: "#F97316", backgroundColor: "#FED7AA" };
    case "Delivery":
    case "Delivered":
      return { color: "#06B6D4", backgroundColor: "#CFFAFE" };
    case "Cancelled":
      return { color: "#EF4444", backgroundColor: "#FEE2E2" };
    case "Completed":
      return { color: "#10B981", backgroundColor: "#D1FAE5" };
    default:
      return { color: "#6B7280", backgroundColor: "#F3F4F6" };
  }
};
